<template lang="html">
    <div class="row pb-4">
        <!-- Left column -->
        <div class="col-md-6 px-5 bg-lightest border-light">
            <div class="mt-4">
                <h5 class="fs-large bold mr-2">Campaign details</h5>
            </div>

            <div class="mt-4">
                <div class="d-flex w-100 mt-3">
                    <div class="d-flex flex-column w-50 fields-min-height mr-3">
                        <label for="code">Code<span class="brand ml-1">*</span></label>
                        
                        <input name="code" type="text" class="" v-model="promocode.code" @input="$v.promocode.code.$touch"/>
                        
                        <div v-if="$v.promocode.code.$invalid">
                            <span v-if="!$v.promocode.code.required" class="fs-small error">Code is required</span>
                            <span v-if="!$v.promocode.code.minLength" class="fs-small error">Code must have at least {{$v.promocode.code.$params.minLength.min}} characters</span>
                        </div>
                    </div>
                    
                    <div class="d-flex flex-column w-50 fields-min-height ml-3">
                        <label for="code">Campaign Type</label>
                        <v-select class="flex-grow-0" :options="getPromocodeAudience" v-model="selectedAudience" @input="audinceChanged"></v-select>
                    </div>
                </div>

                <div class="d-flex w-100 mt-3">
                    <div class="d-flex flex-column w-50 mr-3 fields-min-height">
                        <label for="discount">{{discountLabel}}<span class="brand ml-1">*</span></label>
                        
                        <div class="d-flex">
                            <input name="discount" type="number" class="glued-right w-100" v-model="promocode.value" @input="$v.promocode.value.$touch"/>
                            <v-select class="glued-left select-width" :options="getPercentageOrCurrency" v-model="selectedDiscountType" @input="discountTypeChanged"></v-select>
                        </div>
                        
                        <div v-if="$v.promocode.value.$invalid">
                            <span v-if="!$v.promocode.value.required" class="fs-small error">{{discountLabel}} is required</span>
                        </div>
                    </div>

                    <div class="d-flex flex-column w-50 ml-3 fields-min-height">
                        <label for="max">Max. Quantity<span class="brand ml-1">*</span></label>
                        
                        <input name="max" type="number" placeholder="E.g., 50" class="" v-model="promocode.maxQuantity" @input="$v.promocode.maxQuantity.$touch"/>
                        
                        <div v-if="$v.promocode.maxQuantity.$invalid">
                            <span v-if="!$v.promocode.maxQuantity.required" class="fs-small error">Max. quantity is required</span>
                        </div>
                    </div>
                </div>

                <!-- Radio options per Booking or per Person -->
                <div class="d-flex flex-column my-3">
                    <label>Promocode Type</label>

                    <div class="d-flex mt-2">
                        <radio class="mr-5" name="perperson" v-model="promocode.isMaxQuantityPerPerson" :disabled="disableEdit" :value="true" v-tooltip="disableTooltip">Per Person</radio>
                        <radio name="perbooking" v-model="promocode.isMaxQuantityPerPerson"  :disabled="disableEdit" :value="false" v-tooltip="disableTooltip">Per Booking</radio>
                    </div>
                </div>

				<div class="d-flex flex-column mt-3">
                    <label for="note">Note</label>
                    <textarea name="note" rows="10" v-model="promocode.note" placeholder="Notes about this promocode goes here..."></textarea>
                </div>
            </div>
        </div>

        <!-- Right column -->
        <div class="col-md-6 pr-0">
            <!-- White Box -->
            <div class="col-sm-12 border-light bg-lightest px-5 py-4">
                <h5 class="fs-large bold mb-4">Promocode is valid when booked between</h5>
                
                <!-- Booking period -->
                <div class="d-flex align-items-center">
                    <date-picker-new
                        class="dateRangePicker"
                        v-model="bookingPeriod"
                        @input="bookingPeriodChanged($event)"
                        :range="true"
                        :enableClear="false"
                        :clearOutside="true"
                        :showFromTo="false">
                    </date-picker-new>

                    <span class="mx-3">and</span>
                </div>

                <!-- Valid -->
                <div class="d-flex align-items-center mt-3">
                    <span class="mr-3">
                        <v-select 
                            :options="getPromocodeDaysInAdvance" 
                            v-model="selectedBookingBefore" 
                            @input="bookingBeforeChanged">
                        </v-select>
                    </span>

                    <span>before activity start.</span>
                </div>

                <!-- Validation errors display -->
                <div class="d-flex flex-column" v-if="$v.promocode.bookingPeriodStart.$dirty">
                    <span v-if="!$v.promocode.bookingPeriodStart.required" class="fs-small error">Booking period start is Required</span>
                    <span v-if="!$v.promocode.bookingPeriodStart.lowerThanBookingPeriodFinish" class="fs-small error">Booking period start must be lower or equal to finish date</span>
                </div>

                <div class="d-flex flex-column" v-if="$v.promocode.bookingPeriodFinish.$dirty">
                    <span v-if="!$v.promocode.bookingPeriodFinish.required" class="fs-small error">Booking period end is Required</span>
                    <span v-if="!$v.promocode.bookingPeriodFinish.higherThanBookingPeriodStart" class="fs-small error">Booking period end must be higher or equal to start date</span>
                </div>
            </div>

            <!-- White Box -->
            <div class="col-sm-12 border-light bg-lightest mt-3 px-5 py-4">
                <h5 class="fs-large bold mb-4">For activities that happen between</h5>
                <!-- Start valid period -->
                <div class="d-flex align-items-center">
                    <date-picker-new
                        class="dateRangePicker"
                        v-model="validPeriod"
                        @input="validPeriodFinishChanged($event)"
                        :range="true"
                        :enableClear="false"
                        :clearOutside="true"
                        :showFromTo="false">
                    </date-picker-new>
                </div>

                <!-- Week day picker -->
                <div class="d-flex flex-column pt-4 align-items-start">
                    <label class="mb-1">Day selection<span class="brand ml-1">*</span></label>
                    <div class="d-flex flex-column weeks-min-height">
                        <week-day-picker :dateRange="validPeriod" v-model="promocode.validDaysOfWeek" @change="$v.promocode.validDaysOfWeek.$touch" @hasWeekDays="weekDaysValidation($event)"></week-day-picker>
                    </div>
                </div>

                <!-- Hour Interval selector -->
                <div v-if="haveValidHoursInterval == true" class="pt-3">
                    <label class="mb-1">Starting hour is between</label>
                    <div class="d-flex align-items-center">
                        <!-- Hour Start -->
                        <div style="width: 95px;">
                            <timepicker
                                :defaultTime="validPeriodStartTime"
                                @change="validHourChanged($event, 'start')"
                                :disabled="false">
                            </timepicker>
                        </div>

                        <div class="mx-3">and</div>

                        <!-- Hour end -->
                        <div style="width: 95px;">
                                <timepicker
                                :defaultTime="validPeriodFinishTime"
                                @change="validHourChanged($event, 'finsh')"
                                :disabled="false">
                            </timepicker>
                        </div>
                    </div>

                    <div v-if="$v.promocode.validPeriodHourInterval.$invalid">
                        <span v-if="!$v.promocode.validPeriodHourInterval.isValid" class="fs-small error">Start hour must be before ending hour</span>
                    </div>
                </div>

                <div class="errors-min-height mt-1">
                    <!-- Validation errors display -->
                    <div class="d-flex flex-column" v-if="$v.promocode.validPeriodStart.$invalid">
                        <span v-if="!$v.promocode.validPeriodStart.required" class="fs-small error">Start valid period is Required</span>
                        <span v-if="!$v.promocode.validPeriodStart.lowerThanValidPeriodFinish" class="fs-small error">Start valid period must be lower or equal to finish date</span>
                    </div>
                
                    <div class="d-flex flex-column" v-if="$v.promocode.validPeriodFinish.$invalid">
                        <span v-if="!$v.promocode.validPeriodFinish.required" class="fs-small error">End valid period is Required</span>
                        <span v-if="!$v.promocode.validPeriodFinish.higherThanValidPeriodStart" class="fs-small error">End valid period must be higher or equal to start date</span>
                    </div>
                </div>
            </div>

            <!-- White Box -->
            <div class="col-sm-12 border-light bg-lightest mt-3 px-5 py-4 prod-selection-min-height">
                <h5 class="fs-large bold mb-4">Product selection</h5>

                <!-- Radio options -->
                <div class="d-flex">
                    <radio class="mr-3" name="allProducts" v-model="promocode.isForAllProducts" @input="$v.promocode.products.$touch" :value="true">All Products</radio>
                    <radio name="allProducts" v-model="promocode.isForAllProducts" @input="$v.promocode.products.$touch" :value="false">Selected Products</radio>
                </div>

                <div v-if="promocode.isForAllProducts == true" class="mt-2 text-center p-5 dark">
                    All products will be on this promocode
                </div>

                <!-- Products -->
                <div v-else class="mt-3">
                    <!-- Product list -->
                    <div v-if="promocode.products.length > 0" class="d-flex flex-column">
                        <div v-for="(product, index) in promocode.products" :key="index" class="d-flex align-items-center mb-2">
                            <v-select class="openUp" :options="getProducts" v-model="promocode.products[index]" placeholder="Select a product" label="name"></v-select>
                            <i class="fas fa-trash-alt dark ml-3 pointer" @click="removeProduct(index)"></i>
                        </div>
                    </div>

                    <div v-else class="text-center p-2 dark">
                        Please add a product to this list
                    </div>

                    <!-- Add Btn -->
                    <div class="add d-flex align-items-center pointer fs-small bold mt-3" @click="addProduct">
                        <i class="ico-add fs-smallest add-circle color-1"></i> Add Product
                    </div>

                    <div v-if="$v.promocode.products.$dirty" class="mt-3">
                        <span v-if="!$v.promocode.products.validProduct" class="fs-small error">Select at least one product</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { required, minLength } from 'vuelidate/lib/validators'

import DatePickerNew from '@/tools/DatePicker'
import vSelect from 'vue-select'
import VueDatepicker from 'vuejs-datepicker'
import WeekDayPicker from '@/components/inventory/products/components/WeekDayPicker'
import Timepicker from '@/tools/Timepicker'

export default {
    data() {
        return {
            selectedAudience: null,
            selectedDiscountType: null,
            selectedBookingBefore: null,
            bookingPeriod: [],
            validPeriod: [],
        }
    },
    props: {
        promocode: {
            type: Object,
            required: true
        },
    },
    validations: {
        promocode: {
            code: {
                required,
                minLength: minLength(4),
            },
            value: {
                required,
            },
            maxQuantity: {
                required,
            },
            bookingPeriodStart: {
                required,
                lowerThanBookingPeriodFinish() {
                    return this.validateBookingPeriodStart();
                }
            },
            bookingPeriodFinish: {
                required,
                higherThanBookingPeriodStart() {
                    return this.validateBookingPeriodFinish()
                }
            },
            validPeriodStart: {
                required,
                lowerThanValidPeriodFinish() {
                    return this.validateValidPeriodStart()
                }
            },
            validPeriodFinish: {
                required,
                higherThanValidPeriodStart() {
                    return this.validateValidPeriodFinish()
                }
            },
            validPeriodHourInterval: {
                isValid() {
                    return this.validatePeriodHourInterval()
                }
            },
            validDaysOfWeek: {
                required
            },
            products: {
                validProduct() {
                    return this.validProductSelction()
                }
            },
        },
    },
    watch: {
        promocode: {
            handler(newVal, oldVal) {
                this.emitChange();
            },
            deep: true
        }
    },
    created() {
        // Set the correct dropdown values
        this.setStartingSelections();
        this.setStartingDates();

        // Emits change so that the parent can acces the validation (e.v)
        this.emitChange();

        if (this.getProducts.length === 0) this.initProducts();
    },
    computed: {
        ...mapGetters([
            'getPercentageOrCurrency',
            'getPromocodeAudience',
            'getPromocodeDaysInAdvance',
            'getProducts',
        ]),
        discountLabel() {
            return this.selectedAudience.id == 0? 'Discount' : 'Commission';
        },
        haveValidHoursInterval() {
            return typeof this.promocode.validPeriodStartTime != "undefined" && typeof this.promocode.validPeriodFinishTime != "undefined";
        },
        pickerDateFrom: {
            get() {
                return moment(this.dateFrom).format('YYYY-MM-DD');
            },
            set(value) {
                this.dateFrom = moment(value);
            }
        },
        pickerDateTo: {
            get() {
                return moment(this.dateTo).format('YYYY-MM-DD');
            },
            set(value) {
                this.dateTo = moment(value);
            }
        },
        validPeriodStartTime() {
            let startTimeArray = this.promocode.validPeriodStartTime.split(":");

            return [
                startTimeArray[0],
                startTimeArray[1]
            ]
        },
        validPeriodFinishTime() {
            let finishTimeArray = this.promocode.validPeriodFinishTime.split(":");

            return [
                finishTimeArray[0],
                finishTimeArray[1]
            ]
        },
        disableEdit() {
            if (this.promocode.numberOfUses != 0 && this.promocode.id != undefined ) {
                return true;
            }
        },
        disableTooltip() {
            if (this.disableEdit) {
                return "This can't be changed. Promocode has already been used.";
            }
        },
    },
    methods: {
            ...mapActions([
            'initProducts',
        ]),
        weekDaysValidation(isValid) {
            this.$emit('hasWeekDays', isValid);
        },
        bookingPeriodChanged(e) {
            this.promocode.bookingPeriodStart = e[0];
            this.promocode.bookingPeriodFinish = e[1];
            this.$v.promocode['bookingPeriodStart'].$touch();
            this.$v.promocode['bookingPeriodFinish'].$touch();
        },
        validPeriodFinishChanged(e) {
            this.promocode.validPeriodStart = e[0];
            this.promocode.validPeriodFinish = e[1];
            this.$v.promocode['validPeriodStart'].$touch();
            this.$v.promocode['validPeriodFinish'].$touch();
        },
        validateBookingPeriodStart() {
            return moment(this.promocode.bookingPeriodStart).isSameOrBefore(this.promocode.bookingPeriodFinish);
        },
        validateBookingPeriodFinish() {
            return moment(this.promocode.bookingPeriodFinish).isSameOrAfter(this.promocode.bookingPeriodStart);
        },
        validateValidPeriodStart() {
            return moment(this.promocode.validPeriodStart).isSameOrBefore(this.promocode.validPeriodFinish);
        },
        validateValidPeriodFinish() {
            return moment(this.promocode.validPeriodFinish).isSameOrAfter(this.promocode.validPeriodStart);
        },
        validatePeriodHourInterval() {
            // False = invalid
            if (typeof this.promocode.validPeriodStartTime == "undefined" && this.promocode.validPeriodFinishTime == "undefined") {
                return true;
            } else {
                // Start Hour MUST be before finish hour
                return Date.parse('01/01/2011 '+this.promocode.validPeriodStartTime) < Date.parse('01/01/2011 '+this.promocode.validPeriodFinishTime);
            }
        },
        validHourChanged(e, type) {
            if (type == 'start') {
                this.promocode.validPeriodStartTime = e + ':00';
            } else {
                this.promocode.validPeriodFinishTime = e + ':00';
            }

            this.$v.promocode.validPeriodHourInterval.$touch();
        },
        validProductSelction() {
            if (this.promocode.isForAllProducts == true) {
                return true;
            } else {
                return this.promocode.products.length > 0;
            }
        },
        setStartingSelections() {
            // Set Tourist or Agent
            this.selectedAudience = this.getPromocodeAudience.find(audience => audience.id == this.promocode.promocodeType);

            // Set time booked before
            this.selectedBookingBefore = this.getPromocodeDaysInAdvance.find(option => option.id == this.promocode.bookingDaysInAdvance);

            // Set the correct discount type: € or %
            this.selectedDiscountType = this.getPercentageOrCurrency.find(type => type.id == this.promocode.amountType);
        },
        setStartingDates() {
            this.bookingPeriod[0] = this.promocode.bookingPeriodStart? moment(this.promocode.bookingPeriodStart).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
            this.bookingPeriod[1] = this.promocode.bookingPeriodFinish? moment(this.promocode.bookingPeriodFinish).format('YYYY-MM-DD') : moment().add(8, 'day').format('YYYY-MM-DD');
            this.promocode.bookingPeriodStart = this.bookingPeriod[0];
            this.promocode.bookingPeriodFinish = this.bookingPeriod[1];

            this.validPeriod[0] = this.promocode.validPeriodStart? moment(this.promocode.validPeriodStart).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
            this.validPeriod[1] = this.promocode.validPeriodFinish? moment(this.promocode.validPeriodFinish).format('YYYY-MM-DD') : moment().add(8, 'day').format('YYYY-MM-DD');
            this.promocode.validPeriodStart = this.validPeriod[0];
            this.promocode.validPeriodFinish = this.validPeriod[1];
        },
        audinceChanged(e) {
            this.promocode.promocodeType = e.id;
        },
        bookingBeforeChanged(e) {
            this.promocode.bookingDaysInAdvance = e.id;
        },
        discountTypeChanged(e) {
            this.promocode.amountType = e.id;
        },
        customDateChanged(e, holder) {
            this.$set(this.promocode, holder, moment(e).format("YYYY-MM-DD HH:mm:ss"));
            this.$v.promocode[holder].$touch();
        },
        addProduct() {
            this.promocode.products.push(this.getProducts[0]);
        },
        removeProduct(index) {
            this.promocode.products.splice(index, 1);
        },
        emitChange() {
            this.$emit('change', {promocode: this.promocode, v:this.$v});
        }
    },
    components: {
        vSelect,
        VueDatepicker,
        WeekDayPicker,
        DatePickerNew,
        Timepicker
    }
}
</script>

<style lang="scss" scoped>
    .weeks-min-height {
        min-height: 5.2rem;
    }

    .errors-min-height {
        min-height: 1.2rem;
    }

    .fields-min-height {
        min-height: 5rem;
    }

    .select-width {
        width: 55px;
    }

    .prod-selection-min-height {
        min-height: 16.5rem;
    }
</style>

<style lang="scss">
    .radio-component label {
        display: inline-flex;
        align-items: center;
    }
</style>



// WEBPACK FOOTER //
// promocodeForm.vue?43498b72